/**
 * "👷 What the tech will do" on the office job page — always open, right
 * under the job header, on BOTH the classic and the new page. Being "on
 * track" never folds it away (Corbin approved 2026-10-22).
 *
 *   - The block: one line per device + "For the whole job". The tech sees it
 *     word for word.
 *   - "See what the tech sees": the real tech view (the server's ?as=tech
 *     shape, drawn by the same TechPlanBlock the iPad uses).
 *   - Edit / + Add a line. After the job is on the tech's day, saving shows
 *     "Changed by the office · <time>" on his screen. No bell, no push.
 *   - "Before it goes": parts not ordered or not staged, a booking on a day
 *     they're closed, a holiday, junk on a device record.
 *   - 🔒 Office only: the tech never sees it.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Lock, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { InfoButton } from "@/components/info-button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useTechPlan, type TechAction, type TechPlanOfficeView } from "@/lib/tech-plan";
import { ActionChip, LineFacts, TechPlanBlock } from "./tech-plan-block";
import type { JobProof } from "@/components/proof/job-proof";
import { ProofChip } from "@/components/proof/proof-chip";
import { isQuiet, type ProofFix } from "@/lib/proof";
import { PlanReadStamps } from "./plan-read";
import { TECH_PLAN_HELP } from "./tech-plan-help";

/** Statuses where the tech already has the job on his day. */
const ON_TECHS_DAY = ["scheduled", "dispatched", "confirmed", "en_route", "arrived", "in_progress"];

interface DraftLine {
    key: string;
    assetId: string | null;
    /** Identical devices shown as one line: saved back to each of them. */
    assetIds: string[];
    /** The words as shown, and each device's own words, to tell an edit apart. */
    shown: string;
    memberText: string;
    action: TechAction | null;
    text: string;
}

function blankLine(): DraftLine {
    return { key: `new-${Date.now()}`, assetId: null, assetIds: [], shown: "", memberText: "", action: null, text: "" };
}

export function OfficeTechPlanCard({
    jobId,
    jobStatus,
    proof = null,
}: {
    jobId: string;
    jobStatus: string | null | undefined;
    /** Proof chips S3: where each line came from (null = the How-sure switch is off). */
    proof?: JobProof | null;
}) {
    const q = useTechPlan(jobId);
    const [editing, setEditing] = useState<{ lines: DraftLine[]; jobWide: string } | null>(null);
    const [preview, setPreview] = useState(false);
    const qc = useQueryClient();
    const confirm = useMutation({
        // "It's right": save the block exactly as shown. Nothing changes for
        // the tech (the server doesn't stamp "Changed by the office" on it).
        mutationFn: (plan: TechPlanOfficeView) =>
            api.post<{ ok: true; changedAfterDispatch: boolean }>(`/api/jobs/${jobId}/tech-plan`, {
                lines: plan.lines.flatMap(
                    (l): Array<{ assetId: string | null; action: TechAction | null; text: string }> =>
                        (l.assetIds ?? []).length > 0
                            ? (l.assetIds ?? []).map((id) => ({
                                  assetId: id,
                                  action: l.action,
                                  text: l.memberText ?? l.text,
                              }))
                            : [{ assetId: null, action: l.action, text: l.text }],
                ),
                jobWide: plan.jobWideText,
            }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["job", jobId] });
            toast.success("Saved as it is. Every line now shows you checked it.");
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save. Try again."),
    });

    if (q.isLoading) {
        return (
            <section
                className="rounded-xl border-2 border-[#7a5c0a]/60 bg-card p-4 text-sm text-muted-foreground"
                role="status"
            >
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading what the tech will do…
            </section>
        );
    }
    // A viewer without jobs.manage gets the tech's own view, read-only.
    if (q.data?.audience === "tech") return <TechPlanBlock plan={q.data.plan} preview />;
    if (q.isError || !q.data || q.data.audience !== "office") {
        return (
            <section className="rounded-xl border-2 border-destructive/50 bg-card p-4 text-sm" role="alert">
                Couldn't load what the tech will do.{" "}
                <button type="button" className="font-semibold text-primary underline" onClick={() => void q.refetch()}>
                    Try again
                </button>
            </section>
        );
    }
    const plan = q.data.plan;
    const startEdit = (addLine = false) => {
        const lines: DraftLine[] = plan.lines.map((l) => ({
            key: l.key,
            assetId: l.assetId,
            assetIds: l.assetIds ?? (l.assetId ? [l.assetId] : []),
            shown: l.text,
            memberText: l.memberText ?? l.text,
            action: l.action,
            text: l.text,
        }));
        if (addLine) lines.push(blankLine());
        setEditing({ lines, jobWide: plan.jobWideText });
    };

    return (
        <div className="space-y-2" data-testid="office-tech-plan">
            <section
                aria-label="What the tech will do"
                className="space-y-3 rounded-xl border-2 border-[#7a5c0a] bg-card p-4 dark:border-amber-500/70"
            >
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-extrabold tracking-tight">👷 What the tech will do</h2>
                        <InfoButton topic={TECH_PLAN_HELP.officeBlock} />
                    </div>
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-[#7a5c0a] dark:bg-amber-500/15 dark:text-amber-300">
                        The tech sees this, word for word
                    </span>
                </div>

                {editing ? (
                    <Editor
                        jobId={jobId}
                        plan={plan}
                        draft={editing}
                        setDraft={setEditing}
                        onTechsDay={ON_TECHS_DAY.includes(jobStatus ?? "")}
                        onDone={() => setEditing(null)}
                    />
                ) : (
                    <>
                        <OfficeLines
                            plan={plan}
                            proof={proof}
                            onFix={(fix) => {
                                if (fix.action === "confirm_scope") confirm.mutate(plan);
                                else if (fix.action === "edit_scope") startEdit(false);
                                else if (fix.action === "add_scope_line") startEdit(true);
                            }}
                        />
                        <PlanReadStamps jobId={jobId} />
                        <div className="flex flex-wrap items-center gap-2">
                            <Button size="sm" onClick={() => setPreview((p) => !p)} data-testid="see-what-tech-sees">
                                {preview ? "Hide the tech's view" : "See what the tech sees"}
                            </Button>
                            <InfoButton topic={TECH_PLAN_HELP.preview} />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startEdit(false)}
                                data-testid="tech-plan-edit"
                            >
                                Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => startEdit(true)}>
                                <Plus className="mr-1 h-3.5 w-3.5" /> Add a line
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 border-t border-dashed pt-2 text-xs text-muted-foreground">
                            {plan.sources.map((s) => (
                                <span key={s}>{s}</span>
                            ))}
                            {plan.changedByOffice && (
                                <span>
                                    The tech's screen says “Changed by the office · {plan.changedByOffice.atWords}”
                                    {plan.changedByOffice.byName ? ` (${plan.changedByOffice.byName})` : ""}
                                </span>
                            )}
                        </div>
                        {preview && <TechPreview jobId={jobId} onClose={() => setPreview(false)} />}
                    </>
                )}
            </section>

            {plan.beforeItGoes.length > 0 && (
                <div
                    className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-500/60 dark:bg-amber-500/10"
                    data-testid="before-it-goes"
                >
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5" /> Before it goes
                    </div>
                    <ul className="list-disc space-y-0.5 pl-5">
                        {plan.beforeItGoes.map((b) => (
                            <li key={b}>{b}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div
                className="space-y-1 rounded-lg border border-dashed border-stone-300 bg-stone-50 px-3 py-2.5 text-sm dark:border-stone-600 dark:bg-stone-900"
                data-testid="office-only-box"
            >
                <div className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Office only — the tech doesn't see this
                    </span>
                    <InfoButton topic={TECH_PLAN_HELP.officeOnly} />
                </div>
                {plan.officeOnly.length > 0 ? (
                    plan.officeOnly.map((o, i) => (
                        <p key={i} className="leading-snug text-foreground/85">
                            {o}
                        </p>
                    ))
                ) : (
                    <p className="text-muted-foreground">Nothing office-only on this job.</p>
                )}
            </div>
        </div>
    );
}

function OfficeLines({
    plan,
    proof,
    onFix,
}: {
    plan: TechPlanOfficeView;
    /** Proof chips S3; null with the switch off. */
    proof?: JobProof | null;
    onFix?: (fix: ProofFix) => void;
}) {
    const deviceCount = plan.lines.filter((l) => l.assetId).length;
    const jobItems = [
        ...plan.autoLines.map((a) => ({ icon: a.icon, text: a.text, from: a.from as string | null })),
        ...plan.jobWide.map((j) => ({ icon: j.icon, text: j.text, from: null as string | null })),
    ];
    return (
        <>
            {plan.lines.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    Nothing for the tech yet. Use <b>Add a line</b> to tell him what to do.
                </p>
            ) : (
                <div className="divide-y">
                    {plan.lines.map((l) => (
                        <div
                            key={l.key}
                            className="grid grid-cols-1 gap-1.5 py-2.5 first:pt-0 sm:grid-cols-[84px_minmax(0,1fr)] sm:gap-3"
                            data-testid="office-tech-line"
                        >
                            <ActionChip action={l.action} number={l.number} count={deviceCount} />
                            <div className="min-w-0 space-y-1 sm:col-start-2">
                                <p className="text-[15px] font-semibold leading-snug">
                                    {l.text}
                                    {/* A proven line: the small gray ✓ right after its words. */}
                                    {proof?.scope[l.key] && isQuiet(proof.scope[l.key]!, "busy") && (
                                        <span className="ml-1.5 inline-flex align-[-2px]">
                                            <ProofChip
                                                proof={proof.scope[l.key]!}
                                                place="busy"
                                                onFix={onFix}
                                                testId="scope-proof-chip"
                                            />
                                        </span>
                                    )}
                                </p>
                                {proof?.scope[l.key] && !isQuiet(proof.scope[l.key]!, "busy") && (
                                    <ProofChip
                                        proof={proof.scope[l.key]!}
                                        place="busy"
                                        onFix={onFix}
                                        testId="scope-proof-chip"
                                    />
                                )}
                                <LineFacts line={l} audience="office" />
                                {l.checks.length > 0 && (
                                    <div
                                        className="rounded-md border border-amber-400 bg-amber-50 px-2 py-1.5 text-[13px] leading-snug text-amber-900 dark:bg-amber-500/10 dark:text-amber-200"
                                        data-testid="device-check"
                                    >
                                        <b>Check this device:</b> {l.checks.join(" ")} Fix it on the device so the
                                        tech's line reads right.
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {(proof?.scopeGaps ?? []).length > 0 && (
                <div className="space-y-1.5 border-t pt-2.5" data-testid="scope-gaps">
                    {proof!.scopeGaps.map((g) => (
                        <div key={`${g.assetId}:${g.leg}`} className="space-y-1">
                            <p className="text-sm font-semibold leading-snug">
                                {g.leg === "bypass" ? "Bypass: " : ""}
                                {g.label}
                            </p>
                            <ProofChip proof={g.proof} place="busy" onFix={onFix} testId="scope-gap-chip" />
                        </div>
                    ))}
                </div>
            )}
            {jobItems.length > 0 && (
                <div className="space-y-1 border-t pt-2.5">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        For the whole job
                    </div>
                    <ul className="space-y-1">
                        {jobItems.map((j, i) => (
                            <li key={i} className="grid grid-cols-[22px_minmax(0,1fr)] gap-1 text-sm leading-snug">
                                <span aria-hidden className="text-center">
                                    {j.icon}
                                </span>
                                <span>
                                    {j.text}
                                    {j.from && (
                                        <span className="ml-1.5 text-xs text-muted-foreground">from {j.from}</span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
}

function TechPreview({ jobId, onClose }: { jobId: string; onClose: () => void }) {
    const q = useTechPlan(jobId, { asTech: true });
    return (
        <div className="rounded-xl bg-[#2a2725] p-2.5" data-testid="tech-preview">
            <div className="mb-2 flex items-center justify-between px-1 text-xs font-semibold text-stone-200">
                <span>The tech's iPad, right now</span>
                <button type="button" className="min-h-[36px] px-2 font-bold text-amber-300" onClick={onClose}>
                    Close
                </button>
            </div>
            {q.data?.audience === "tech" ? (
                <TechPlanBlock plan={q.data.plan} preview />
            ) : (
                <div className="rounded-lg bg-white p-4 text-sm text-stone-600">
                    {q.isError ? "Couldn't load the tech's view." : "Loading the tech's view…"}
                </div>
            )}
        </div>
    );
}

function Editor({
    jobId,
    plan,
    draft,
    setDraft,
    onTechsDay,
    onDone,
}: {
    jobId: string;
    plan: TechPlanOfficeView;
    draft: { lines: DraftLine[]; jobWide: string };
    setDraft: (d: { lines: DraftLine[]; jobWide: string } | null) => void;
    onTechsDay: boolean;
    onDone: () => void;
}) {
    const qc = useQueryClient();
    const save = useMutation({
        mutationFn: () =>
            api.post<{ ok: true; changedAfterDispatch: boolean }>(`/api/jobs/${jobId}/tech-plan`, {
                // A grouped line goes back to every device it covers; left as
                // shown, each keeps its own words.
                lines: draft.lines.flatMap(
                    (l): Array<{ assetId: string | null; action: TechAction | null; text: string }> =>
                        l.assetIds.length > 0
                            ? l.assetIds.map((id) => ({
                                  assetId: id,
                                  action: l.action,
                                  text: l.text === l.shown ? l.memberText : l.text,
                              }))
                            : [{ assetId: null, action: l.action, text: l.text }],
                ),
                jobWide: draft.jobWide,
            }),
        onSuccess: (r) => {
            void qc.invalidateQueries({ queryKey: ["job", jobId] });
            void qc.invalidateQueries({ queryKey: ["activity-timeline"] });
            toast.success(r.changedAfterDispatch ? "Saved. The tech's screen says “Changed by the office.”" : "Saved");
            onDone();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save. Try again."),
    });
    const labelOne = (assetId: string | null) => plan.devices.find((d) => d.assetId === assetId)?.label ?? null;
    const labelFor = (l: DraftLine) =>
        l.assetIds.length > 1 ? `${l.assetIds.length} devices, same work` : labelOne(l.assetId);
    const set = (key: string, text: string) =>
        setDraft({ ...draft, lines: draft.lines.map((l) => (l.key === key ? { ...l, text } : l)) });
    return (
        <div className="space-y-3" data-testid="tech-plan-editor">
            {onTechsDay && (
                <p className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:bg-blue-500/10 dark:text-blue-200">
                    This job is already on the tech's day. When you save, his screen will say “Changed by the office”.
                    He won't get a ping, so call him if it's urgent.
                </p>
            )}
            {draft.lines.map((l) => (
                <div key={l.key} className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <ActionChip action={l.action} />
                        <span className="truncate">
                            {l.assetId ? labelFor(l) : "A line that isn't about one device"}
                        </span>
                        {!l.assetId && (
                            <button
                                type="button"
                                className="ml-auto inline-flex min-h-[32px] items-center gap-1 text-destructive"
                                onClick={() =>
                                    setDraft({ ...draft, lines: draft.lines.filter((x) => x.key !== l.key) })
                                }
                            >
                                <Trash2 className="h-3.5 w-3.5" /> Remove
                            </button>
                        )}
                    </div>
                    <Textarea
                        rows={2}
                        value={l.text}
                        onChange={(e) => set(l.key, e.target.value)}
                        placeholder={
                            l.assetId ? "What to do to this device, and what to bring" : "e.g. Bring the long ladder"
                        }
                        aria-label={l.assetId ? `Line for ${labelFor(l)}` : "Extra line"}
                    />
                </div>
            ))}
            <Button
                size="sm"
                variant="outline"
                onClick={() =>
                    setDraft({
                        ...draft,
                        lines: [...draft.lines, blankLine()],
                    })
                }
            >
                <Plus className="mr-1 h-3.5 w-3.5" /> Add a line
            </Button>
            <div className="space-y-1">
                <label
                    className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
                    htmlFor="tp-jobwide"
                >
                    For the whole job{" "}
                    <span className="font-normal normal-case tracking-normal">(one thing per line)</span>
                </label>
                <Textarea
                    id="tp-jobwide"
                    rows={4}
                    value={draft.jobWide}
                    onChange={(e) => setDraft({ ...draft, jobWide: e.target.value })}
                    placeholder={"Call Rafael 30 minutes before you get there\nIVR check-in: 844-347-3487"}
                />
                <p className="text-xs text-muted-foreground">
                    Gate codes and the return reason fill in on their own. You don't need to type them.
                </p>
            </div>
            <div className={cn("flex items-center gap-2")}>
                <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending} data-testid="tech-plan-save">
                    {save.isPending ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDraft(null)} disabled={save.isPending}>
                    Cancel
                </Button>
            </div>
        </div>
    );
}
